import { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function EmailCaptureSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        formRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 75%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = () => {
    setSubmitted(true);
  };

  return (
    <section
      id="email-capture"
      ref={sectionRef}
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-2xl mx-auto text-center">
        <div ref={formRef}>
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
            Stay in the room
          </p>

          <h2 className="font-heading text-xl md:text-3xl lg:text-5xl leading-[1.2] text-[#1A1A1A] mb-5">
            Not ready yet? I will be here when you are.
          </h2>

          <p className="font-body text-sm md:text-base text-[#4A4A4A] leading-relaxed mb-10">
            Every week, Mandie writes to her inner circle, honest, unhurried letters about healing, identity, and what it really takes to come back to yourself. No performance. No noise. Just the kind of words that find you exactly when you need them. Women tell her they save these letters. Read them more than once. Share them with a friend going through something they cannot name. If that sounds like something you need right now, even if you are not ready for the work yet, you are welcome here.
          </p>

          {/* Gold Divider */}
          <div className="w-16 h-px bg-[#C9A84C] mx-auto mb-10" />

          {submitted ? (
            <div className="bg-[#F5F0ED] border border-[#C9A84C]/30 px-8 py-10">
              <p className="font-heading text-xl text-[#1A1A1A] mb-3">
                Welcome to the inner circle.
              </p>
              <p className="font-body text-sm text-[#4A4A4A]">
                You are now on the list. Watch your inbox for healing letters from Mandie.
              </p>
            </div>
          ) : (
            <form
              id="newsletter-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d8fl0795ptqq13rvl8b0"
              method="POST"
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-4 items-stretch"
            >
              <input
                type="text"
                name="first_name"
                placeholder="First name"
                required
                className="flex-1 px-5 py-3.5 bg-[#FDFAF8] border border-[#C9A84C]/30 text-[#1A1A1A] text-sm font-body rounded-lg focus:outline-none focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]/30 transition-colors"
              />
              <input
                type="email"
                name="email"
                placeholder="Email address"
                required
                className="flex-1 px-5 py-3.5 bg-[#FDFAF8] border border-[#C9A84C]/30 text-[#1A1A1A] text-sm font-body rounded-lg focus:outline-none focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]/30 transition-colors"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center px-7 py-3.5 bg-[#D4006A] text-white text-sm font-body font-medium rounded-full hover:bg-[#B3005A] transition-colors duration-300 whitespace-nowrap"
              >
                Yes, I&apos;m in
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}